import { faFileImport } from "@fortawesome/free-solid-svg-icons/faFileImport";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { FontSize } from "../../../../components/Text";
import { useTheme } from "../../../../ThemeContext";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { EntityIcons } from "../../../../utils/GlobalIcons";
import { useDrawersContext } from "../../../tasks/drawers-context";
import {
  SetupCard,
  SetupCardActions,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";
import { DashboardSetupCardLabel } from "./DashboardSetupCardLabel";

const MiniTable = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  border-radius: ${Rounded.md};
  border: 1px solid ${({ theme }) => theme.sub};
  overflow: hidden;
  font-size: ${FontSize.s};
`;

const HeadCell = styled.span`
  padding: ${Spacing.xs} ${Spacing.s};
  background-color: ${({ theme }) => theme.darkMain};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
`;

const Cell = styled.span`
  display: flex;
  align-items: center;
  height: 2rem;
  padding-inline: ${Spacing.s};
  border-top: 1px solid ${({ theme }) => theme.sub};
`;

const Ghost = styled.span<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 0.5rem;
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
`;

const EntryActions = styled(SetupCardActions)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));

  & > button {
    width: 100%;
  }
`;

type DashboardSetupEntryCardProps = {
  featured?: boolean;
  done?: boolean;
};

export const DashboardSetupEntryCard: React.FC<
  DashboardSetupEntryCardProps
> = ({ featured, done }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { setEntryCreateVisible, setImportExportVisible } = useDrawersContext();

  return (
    <SetupCard
      $featured={featured}
      $done={done}
      data-testid={TestIDs.dashboard.setup.card("ENTRY")}
    >
      <DashboardSetupCardLabel featured={featured} done={done} />
      <SetupCardTitle $featured={featured} $done={done}>
        {t("dashboard.setup.entry.title")}
      </SetupCardTitle>
      <SetupCardDescription $done={done}>
        {t("dashboard.setup.entry.description")}
      </SetupCardDescription>

      <MiniTable aria-hidden>
        <HeadCell>{t("common.name")}</HeadCell>
        <HeadCell>{t("common.phone")}</HeadCell>
        <Cell>
          <Ghost $width="70%" />
        </Cell>
        <Cell>
          <Ghost $width="55%" />
        </Cell>
        <Cell>
          <Ghost $width="50%" />
        </Cell>
        <Cell>
          <Ghost $width="65%" />
        </Cell>
      </MiniTable>

      <EntryActions>
        <Button
          icon={EntityIcons.Entries}
          onClick={() => setEntryCreateVisible(true)}
          dataTestId={TestIDs.dashboard.setup.cta("ENTRY")}
        >
          {t("dashboard.setup.entry.manual")}
        </Button>
        <Button
          icon={faFileImport}
          color="main"
          backgroundColor="white"
          border={`1px solid ${theme.main}`}
          hoverBackgroundColor="sub"
          hoverColor="main"
          onClick={() => {
            setImportExportVisible(true);
            navigate("/entries");
          }}
        >
          {t("dashboard.setup.entry.import")}
        </Button>
      </EntryActions>
    </SetupCard>
  );
};
